/**
 * OS-level browser notifications (Notification API).
 * Works when the tab is open but minimized / in background.
 * Fully closed browser needs a service-worker push setup (not covered here).
 */

const PERMISSION_ASKED_KEY = "safem_notification_permission_asked";

export const notificationsSupported = () =>
  typeof window !== "undefined" && "Notification" in window;

export const notificationPermission = () => {
  if (!notificationsSupported()) {
    return "unsupported";
  }
  return Notification.permission; // "default" | "granted" | "denied"
};

export const canShowBrowserNotifications = () =>
  notificationsSupported() && Notification.permission === "granted";

/**
 * Ask the user for notification permission (browser OS prompt).
 * Call from a user gesture (e.g. right after Login click).
 * @returns {Promise<"granted"|"denied"|"default"|"unsupported">}
 */
export const requestNotificationPermission = async () => {
  if (!notificationsSupported()) {
    return "unsupported";
  }

  if (Notification.permission === "granted") {
    return "granted";
  }

  if (Notification.permission === "denied") {
    return "denied";
  }

  try {
    const result = await Notification.requestPermission();
    try {
      localStorage.setItem(PERMISSION_ASKED_KEY, "1");
    } catch (_) {
      /* ignore */
    }
    return result;
  } catch (err) {
    console.warn("Notification permission request failed:", err);
    return Notification.permission;
  }
};

/**
 * If permission was never decided, ask once (e.g. after login / first dashboard load).
 */
export const ensureNotificationPermissionAsked = async () => {
  if (!notificationsSupported()) {
    return "unsupported";
  }
  if (Notification.permission !== "default") {
    return Notification.permission;
  }
  return requestNotificationPermission();
};

/**
 * Show an OS notification for a new room order.
 * No-op if permission was not granted.
 *
 * @param {object} order  API / event order row with room + menu
 * @param {{ onClick?: () => void }} [options]
 */
export const showOrderBrowserNotification = (order, { onClick } = {}) => {
  if (!canShowBrowserNotifications() || !order) {
    return null;
  }

  const roomName = order?.room?.name || `Room #${order.room_id ?? ""}`;
  const itemName = order?.menu?.name || "New item";
  const qty = order?.qty != null ? ` × ${order.qty}` : "";
  const title = `New Order — ${roomName}`;
  const body = `${itemName}${qty}`;

  try {
    const notification = new Notification(title, {
      body,
      tag: `safem-order-${order.id}`,
      renotify: true,
      requireInteraction: false,
    });

    notification.onclick = () => {
      try {
        window.focus();
      } catch (_) {
        /* ignore */
      }
      onClick?.();
      notification.close();
    };

    return notification;
  } catch (err) {
    console.warn("Unable to show browser notification:", err);
    return null;
  }
};

/**
 * From a Reverb OrderChanged payload (action === "created").
 */
export const showOrderBrowserNotificationFromPayload = (
  payload,
  { onClick } = {}
) => {
  if (payload?.action !== "created" || !payload?.order) {
    return null;
  }
  return showOrderBrowserNotification(payload.order, { onClick });
};
